/**
 * @spec docs/specs/plans/chip-migration-program.md
 * Chain configuration and network utilities (Base only post CHIP migration)
 */
import { env } from './env';

/**
 * Chain configuration
 */
export interface ChainConfig {
  id: number;
  name: string;
  rpcUrl: string;
  explorerUrl: string;
  explorerName: string;
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
}

/**
 * Supported chains.
 *
 * Polygon was removed from the supported set as part of the CHIP migration.
 * `getChainById`, `isSupportedChain`, and Privy's chain list now only see
 * Base. The legacy Polygon explorer URL (still needed by the bridge / swap /
 * USDT-redemption views until they are torn down by the cross-chain decom
 * workstream) is exposed separately as `LEGACY_POLYGON_CONFIG` so it
 * intentionally does NOT appear in supported-chain checks.
 */
export const CHAIN_CONFIG = {
  BASE: {
    id: 8453,
    name: 'Base',
    rpcUrl: env.baseRpcUrl,
    explorerUrl: env.baseExplorerUrl,
    explorerName: 'Basescan',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  },
  BASE_SEPOLIA: {
    id: 84532,
    name: 'Base Sepolia',
    rpcUrl: env.baseRpcUrl,
    explorerUrl: env.baseExplorerUrl,
    explorerName: 'Basescan',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  },
} as const satisfies Record<string, ChainConfig>;

/**
 * @deprecated Legacy Polygon mainnet metadata for the bridge / swap / USDT
 * redemption views. Slated for deletion by the cross-chain decom workstream.
 * Intentionally not part of `CHAIN_CONFIG` so `isSupportedChain` correctly
 * returns false for chainId 137.
 */
export const LEGACY_POLYGON_CONFIG: ChainConfig = {
  id: 137,
  name: 'Polygon',
  rpcUrl: env.polygonRpcUrl,
  explorerUrl: env.polygonExplorerUrl,
  explorerName: 'Polygonscan',
  nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
};

export type SupportedChainId = (typeof CHAIN_CONFIG)[keyof typeof CHAIN_CONFIG]['id'];

/**
 * Get chain config by ID
 */
export function getChainById(chainId: number): ChainConfig | undefined {
  return Object.values(CHAIN_CONFIG).find((chain) => chain.id === chainId);
}

/**
 * Check if a chain ID is supported
 */
export function isSupportedChain(chainId: number): chainId is SupportedChainId {
  return Object.values(CHAIN_CONFIG).some((chain) => chain.id === chainId);
}

/**
 * Get explorer URL for a transaction
 */
export function getTransactionUrl(chainId: number, txHash: string): string {
  const chain = getChainById(chainId);
  if (!chain) return '';
  return `${chain.explorerUrl}/tx/${txHash}`;
}

/**
 * Get explorer URL for an address
 */
export function getAddressUrl(chainId: number, address: string): string {
  const chain = getChainById(chainId);
  if (!chain) return '';
  return `${chain.explorerUrl}/address/${address}`;
}

/**
 * Get the chain ID for staking operations (Base mainnet or testnet)
 */
export function getStakingChainId(): number {
  // Use Base Sepolia if RPC URL contains 'sepolia', otherwise mainnet
  if (env.baseRpcUrl?.includes('sepolia')) {
    return CHAIN_CONFIG.BASE_SEPOLIA.id;
  }
  return CHAIN_CONFIG.BASE.id;
}
